import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Snackbar from '@mui/material/Snackbar';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import paymentsApi from '../../services/paymentsApi';

/**
 * Upload New Batch — the UI for the server-authoritative import pipeline
 * (POST /api/payments/batches/import). Flow:
 *   pick file -> server preview (no writes) -> confirm -> land on the batch.
 * A previously-uploaded file (same hash) 409s until "import anyway" is checked.
 */
function Field({ label, value }) {
  return (
    <Grid item xs={6} sm={3}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value ?? '—'}</Typography>
    </Grid>
  );
}

function UploadBatch() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [duplicateOf, setDuplicateOf] = useState(null);
  const [acknowledgeDuplicate, setAcknowledgeDuplicate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const notify = (message, severity = 'error') => setSnackbar({ message, severity });

  const requestPreview = async (selected, ack) => {
    setBusy(true);
    setPreview(null);
    setDuplicateOf(null);
    try {
      const res = await paymentsApi.importBatch(selected, { confirm: false, acknowledgeDuplicate: ack });
      setPreview(res.preview);
      setDuplicateOf(res.duplicateOf);
    } catch (e) {
      if (e.status === 409 && e.body?.duplicateOf) {
        // Duplicate file: show the preview it carried and require acknowledgement.
        setPreview(e.body.preview || null);
        setDuplicateOf(e.body.duplicateOf);
      } else {
        notify(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleFileChange = (event) => {
    const selected = event.target.files && event.target.files[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!selected) return;
    setFile(selected);
    setAcknowledgeDuplicate(false);
    requestPreview(selected, false);
  };

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const res = await paymentsApi.importBatch(file, { confirm: true, acknowledgeDuplicate });
      if (res.storageWarning) {
        notify(`Batch created; raw-file storage warning: ${res.storageWarning}`, 'warning');
      }
      navigate(`/payments/batches/${res.batch.id}`);
    } catch (e) {
      notify(e.message);
    } finally {
      setBusy(false);
    }
  };

  const canConfirm =
    !busy && file && preview && preview.valid && (!duplicateOf || acknowledgeDuplicate);

  return (
    <Box sx={{ mt: 3, maxWidth: 900, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>Upload New Batch</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Import a weekly vendor payment file (.csv or .xlsx). The server validates the
          file and shows a preview before anything is created; after you confirm, the
          rules engine and payment calculation run automatically.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button variant="contained" component="label" startIcon={<CloudUploadIcon />} disabled={busy}>
            Choose file
            <input type="file" accept=".csv,.xlsx" hidden onChange={handleFileChange} />
          </Button>
          {file && <Typography variant="body2">{file.name}</Typography>}
          {busy && <CircularProgress size={22} />}
        </Box>

        {duplicateOf && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>This file appears to have already been uploaded</AlertTitle>
            Existing batch <strong>{duplicateOf.batch_number}</strong> ({duplicateOf.status}) —
            uploaded {duplicateOf.uploaded_at ? new Date(duplicateOf.uploaded_at).toLocaleString() : '—'}
            {duplicateOf.uploaded_file_name ? ` as "${duplicateOf.uploaded_file_name}"` : ''}.
            <FormControlLabel
              sx={{ display: 'block', mt: 1 }}
              control={
                <Checkbox
                  checked={acknowledgeDuplicate}
                  onChange={(e) => {
                    setAcknowledgeDuplicate(e.target.checked);
                    if (e.target.checked && !preview) requestPreview(file, true);
                  }}
                />
              }
              label="Import anyway (this will be recorded)"
            />
          </Alert>
        )}

        {preview && (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Field
                label="Detected template"
                value={preview.detectedTemplate ? `${preview.detectedTemplate.name} (${preview.detectedTemplate.version})` : 'No match'}
              />
              <Field label="Invoice week" value={preview.invoiceWeek} />
              <Field label="Rows" value={preview.rowCount} />
              <Field label="File" value={`${preview.fileType?.toUpperCase() || ''} · ${((preview.sizeBytes || 0) / 1024).toFixed(0)} KB`} />
            </Grid>

            {(preview.errors || []).map((e) => (
              <Alert key={e} severity="error" sx={{ mb: 1 }}>{e}</Alert>
            ))}
            {(preview.warnings || []).map((w) => (
              <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>
            ))}
            {preview.valid && (
              <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ mb: 2 }}>
                File is valid and ready to import.
              </Alert>
            )}

            {(preview.sampleRows || []).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sample of first {Math.min(5, preview.sampleRows.length)} rows (normalized)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Provider</TableCell>
                      <TableCell>Service type</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">SVC PAY min</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.sampleRows.slice(0, 5).map((r) => (
                      <TableRow key={r.source_row_number}>
                        <TableCell>{r.source_row_number}</TableCell>
                        <TableCell>{r.normalized?.uploaded_provider_name || '—'}</TableCell>
                        <TableCell>{r.normalized?.uploaded_service_type || '—'}</TableCell>
                        <TableCell>{r.normalized?.customer_name || '—'}</TableCell>
                        <TableCell align="right">{r.normalized?.svc_pay_minutes ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Button variant="contained" size="large" onClick={handleConfirm} disabled={!canConfirm}>
              {busy ? 'Importing…' : 'Confirm import'}
            </Button>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Alert severity={snackbar?.severity || 'error'} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UploadBatch;
